import React, { useState, useEffect } from 'react';
import { 
  User, 
  Settings, 
  CreditCard, 
  Shield, 
  BarChart3, 
  Bookmark, 
  Download, 
  FileText,
  Bell,
  Key,
  LogOut,
  Mail,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Filter
} from 'lucide-react';
import { stripeService } from '../lib/stripeService';
import { billingApi, type BillingPlan, type BillingSubscriptionPayload, type BillingCycle, type StripeCardPaymentMethod, type InvoiceRow } from '../lib/billingApi';
import { SpamFilerSettings } from '../components/SpamFilerSettings';
import { WatchlistNotificationSettings } from '../components/WatchlistNotificationSettings';
import { supabase } from '../lib/supabase';
import { track } from '../utils/analytics';
import { PaymentMethodCard } from '../components/billing/PaymentMethodCard';
import { BillingHistoryTable } from '../components/billing/BillingHistoryTable';
import { UsageChart } from '../components/billing/UsageChart';
import { OveragePolicyCard } from '../components/billing/OveragePolicyCard';
import { PlanComparisonModal } from '../components/billing/PlanComparisonModal';

type TabType = 'profile' | 'billing' | 'security' | 'usage' | 'notifications' | 'api-keys' | 'bookmarks' | 'downloads' | 'spam-filters';

export const AccountPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [billingLoadError, setBillingLoadError] = useState<string | null>(null);
  const [subscriptionPayload, setSubscriptionPayload] = useState<BillingSubscriptionPayload | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<StripeCardPaymentMethod | null>(null);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [invoicesHasMore, setInvoicesHasMore] = useState(false);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [fullName, setFullName] = useState<string>('');
  const [organization, setOrganization] = useState<string>('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Allow deep-linking to a specific tab (e.g., Stripe success_url -> /account?tab=billing)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab') as TabType | null;
    if (tab && ['profile', 'billing', 'usage', 'notifications', 'spam-filters'].includes(tab)) {
      setActiveTab(tab);
      params.delete('tab');
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash || ''}`;
      window.history.replaceState({}, document.title, next);
    }
  }, []);

  // Get user ID + profile fields (email is authoritative from auth; name and
  // organization live in user_metadata).
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id || null);
      setUserEmail(user?.email || '');
      setFullName((user?.user_metadata?.full_name as string) || '');
      setOrganization((user?.user_metadata?.organization as string) || '');
    };
    getUser();
  }, []);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setProfileSaved(false);
    setProfileError(null);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { full_name: fullName.trim(), organization: organization.trim() },
      });
      if (error) throw error;
      setProfileSaved(true);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const loadBillingData = async () => {
    setLoadingBilling(true);
    setBillingLoadError(null);
    try {
      const [subResult, pmResult, invResult, plansResult] = await Promise.allSettled([
        billingApi.getSubscription(6),
        billingApi.getPaymentMethod(),
        billingApi.getInvoices({ limit: 10 }),
        billingApi.getPlans()
      ]);

      if (subResult.status === 'fulfilled') {
        setSubscriptionPayload(subResult.value);
      } else {
        setSubscriptionPayload(null);
      }

      if (pmResult.status === 'fulfilled') {
        setPaymentMethod(pmResult.value.payment_method);
      } else {
        setPaymentMethod(null);
      }

      if (invResult.status === 'fulfilled') {
        setInvoices(invResult.value.invoices ?? []);
        setInvoicesHasMore(Boolean(invResult.value.has_more));
      } else {
        setInvoices([]);
        setInvoicesHasMore(false);
      }

      if (plansResult.status === 'fulfilled') {
        setPlans(plansResult.value.plans ?? []);
      } else {
        setPlans([]);
      }

      // If any failed, show a single actionable error
      const failures = [subResult, pmResult, invResult].filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
      if (failures.length > 0) setBillingLoadError('We had trouble loading your billing data. Please try again.');

      // Analytics
      const currentTier = subResult.status === 'fulfilled' ? (subResult.value.subscription?.tier ?? 'free') : 'unknown';
      const usage = subResult.status === 'fulfilled' ? subResult.value.usage.current_period.comment_summaries : 0;
      const limit = subResult.status === 'fulfilled' ? subResult.value.limit.comment_summaries : 0;
      const usagePct = limit > 0 ? usage / limit : 0;
      track('Billing Page Viewed', { current_tier: currentTier, usage_percentage: usagePct });
    } catch (error) {
      console.error('Error loading billing data:', error);
      setSubscriptionPayload(null);
      setPaymentMethod(null);
      setInvoices([]);
      setInvoicesHasMore(false);
      setBillingLoadError('We had trouble loading your billing data. Please try again.');
    } finally {
      setLoadingBilling(false);
    }
  };

  // Load billing data when billing tab is active
  useEffect(() => {
    if (activeTab === 'billing') {
      loadBillingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);


  const tabs = [
    {
      id: 'profile' as TabType,
      label: 'Profile',
      icon: User,
      description: 'Manage your personal information'
    },
    {
      id: 'billing' as TabType,
      label: 'Billing & Plans',
      icon: CreditCard,
      description: 'View billing history and manage subscription'
    },
    {
      id: 'usage' as TabType,
      label: 'Usage & Limits',
      icon: BarChart3,
      description: 'Track your usage and limits'
    },
    {
      id: 'notifications' as TabType,
      label: 'Notifications',
      icon: Bell,
      description: 'Email and notification preferences'
    },
    // Security / API Keys / Bookmarks / Downloads tabs are hidden until their
    // features exist — they were pure "coming soon" placeholders with dead
    // buttons, which reads as broken to a paying customer.
    {
      id: 'spam-filters' as TabType,
      label: 'Spam Filters',
      icon: Filter,
      description: 'Manage spam filer list'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-black mb-3">Profile Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="fld-AccountPagetsx-0">Email Address</label>
                  <input
                    type="email"
                    value={userEmail}
                    disabled
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-500"
                   id="fld-AccountPagetsx-0"/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="fld-AccountPagetsx-1">Full Name</label>
                  <input
                    type="text"
                    placeholder="Enter your full name"
                    value={fullName}
                    onChange={(e) => { setFullName(e.target.value); setProfileSaved(false); }}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                   id="fld-AccountPagetsx-1"/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="fld-AccountPagetsx-2">Organization</label>
                  <input
                    type="text"
                    placeholder="Enter your organization"
                    value={organization}
                    onChange={(e) => { setOrganization(e.target.value); setProfileSaved(false); }}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                   id="fld-AccountPagetsx-2"/>
                </div>
                {profileError && (
                  <p className="text-sm text-red-600">{profileError}</p>
                )}
                <button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                >
                  {savingProfile ? 'Saving…' : profileSaved ? 'Saved ✓' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        );

      case 'billing':
        return (() => {
          const sub = subscriptionPayload?.subscription;
          const usage = subscriptionPayload?.usage.current_period.comment_summaries ?? 0;
          const limit = subscriptionPayload?.limit.comment_summaries ?? 0;
          const pct = limit > 0 ? Math.min((usage / limit) * 100, 100) : 0;

          const formatDate = (dateString: string | null | undefined) => {
            if (!dateString) return 'N/A';
            return new Date(dateString).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
          };

          const currentPlan = plans.find((p) => p.tier === (sub?.tier as any)) || null;
          const currentPriceCents =
            currentPlan && sub?.billing_cycle
              ? (sub.billing_cycle === 'annual' ? Math.round(currentPlan.price_annual_cents / 12) : currentPlan.price_monthly_cents)
              : 0;

          const handleManage = async () => {
            try {
              track('Billing Portal Opened');
              const url = await stripeService.createPortalSession();
              if (url) window.location.href = url;
            } catch (e) {
              console.error(e);
              track('Billing Portal Failed');
              const msg = e instanceof Error ? e.message : String(e);
              if (msg.toLowerCase().includes('stripe not configured')) {
                alert('Billing is not configured yet. Please contact support or try again later.');
              } else {
                alert('Failed to open billing portal. Please try again.');
              }
            }
          };

          const handleSelectPlan = async (tier: BillingPlan['tier'], billingCycle: BillingCycle) => {
            try {
              setCheckoutLoading(true);
              track('Plan Selected', { selected_tier: tier, billing_cycle: billingCycle });
              // Launch: only Pro is purchased via checkout; Free is $0.
              if (tier === 'free') return;
              const { url } = await stripeService.createCheckoutSession(billingCycle);
              if (url) window.location.href = url;
            } catch (e) {
              console.error(e);
              alert('Failed to start checkout. Please try again.');
            } finally {
              setCheckoutLoading(false);
            }
          };

          const loadMoreInvoices = async () => {
            if (loadingBilling) return;
            const last = invoices[invoices.length - 1];
            if (!last) return;
            try {
              setLoadingBilling(true);
              track('Invoices Load More Clicked', { starting_after: last.id });
              const res = await billingApi.getInvoices({ limit: 10, starting_after: last.id });
              setInvoices((prev) => [...prev, ...(res.invoices ?? [])]);
              setInvoicesHasMore(Boolean(res.has_more));
            } catch (e) {
              console.error(e);
              alert('Failed to load more invoices.');
            } finally {
              setLoadingBilling(false);
            }
          };

          return (
            <div className="space-y-4">
              {/* Header actions */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-black">Billing &amp; Plans</h3>
                  <p className="text-gray-600 text-sm">Subscription, usage, payment method, and invoices</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      track('Upgrade Button Clicked', { from_usage_limit: false });
                      setShowPlansModal(true);
                    }}
                    className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors disabled:opacity-50"
                    disabled={plansLoading || checkoutLoading}
                  >
                    View plans
                  </button>
                  {sub && sub.tier !== 'free' && (
                    <button
                      onClick={handleManage}
                      className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium transition-colors"
                    >
                      Manage subscription
                    </button>
                  )}
                </div>
              </div>

              {billingLoadError && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-orange-800 text-sm">{billingLoadError}</p>
                </div>
              )}

              {/* Billing Status */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-black mb-2">Billing Status</h4>
                <div className="text-sm text-gray-700">
                  {sub?.status === 'past_due' && (
                    <div className="flex items-center gap-2 text-orange-700">
                      <AlertTriangle className="w-4 h-4" />
                      <span>Payment past due. Update your payment method in the billing portal.</span>
                    </div>
                  )}
                  {sub?.status === 'cancelled' && (
                    <div className="flex items-center gap-2 text-red-700">
                      <XCircle className="w-4 h-4" />
                      <span>Subscription cancelled.</span>
                    </div>
                  )}
                  {sub?.status === 'active' && (
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span>Active</span>
                    </div>
                  )}
                  {!sub && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <User className="w-4 h-4" />
                      <span>Free plan — no active subscription.</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Current Month Usage */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-black mb-3">Current Period Usage</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Comment summaries</span>
                    <span className="font-medium text-black">
                      {usage} / {limit || '∞'}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-orange-500' : 'bg-green-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-600">
                    Billing period ends {formatDate(sub?.current_period_end ?? null)} (resets on your billing anniversary date).
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <UsageChart history={(subscriptionPayload?.usage.historical ?? []).map((h) => ({ period_start: h.period_start, comment_summaries: h.comment_summaries }))} limit={limit} />
                <OveragePolicyCard currentUsage={usage} limit={limit} tier={sub?.tier ?? 'free'} onUpgrade={() => setShowPlansModal(true)} />
              </div>

              {/* Billing Information */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-black mb-3">Billing Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current tier</span>
                    <span className="font-medium text-black">{sub?.tier_display_name ?? 'Free'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Billing cycle</span>
                    <span className="font-medium text-black">{sub?.billing_cycle ?? 'monthly'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Next billing date</span>
                    <span className="font-medium text-black">{formatDate(sub?.next_billing_date ?? null)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Price</span>
                    <span className="font-medium text-black">
                      {currentPlan ? `$${Math.round(currentPriceCents / 100)}/mo` : '—'}
                    </span>
                  </div>
                </div>

                {sub?.cancel_at_period_end && (
                  <div className="mt-3 p-3 rounded-md bg-yellow-50 border border-yellow-200 text-sm text-yellow-800">
                    Your subscription will end at the period end ({formatDate(sub.current_period_end)}).
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {sub && sub.tier !== 'free' ? (
                  <PaymentMethodCard paymentMethod={paymentMethod} loading={loadingBilling} onManage={handleManage} />
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-black mb-2">Payment Method</h4>
                    <p className="text-sm text-gray-700">
                      You’re on the Free plan. You’ll add a payment method during checkout when you upgrade.
                    </p>
                  </div>
                )}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-black mb-2">Need help?</h4>
                  <p className="text-sm text-gray-700">
                    If something looks wrong with your billing, contact support and include your account email.
                  </p>
                  <a href="/support" className="inline-block mt-3 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium">
                    Contact support
                  </a>
                </div>
              </div>

              <BillingHistoryTable invoices={invoices} loading={loadingBilling} hasMore={invoicesHasMore} onLoadMore={loadMoreInvoices} />

              <PlanComparisonModal
                isOpen={showPlansModal}
                onClose={() => setShowPlansModal(false)}
                plans={plans}
                currentTier={sub?.tier ?? 'free'}
                onSelect={handleSelectPlan}
              />
            </div>
          );
        })();

      case 'security':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-black mb-3">Password</h3>
              <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                Change Password
              </button>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-black mb-3">Two-Factor Authentication</h3>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-yellow-800">Not Enabled</h4>
                    <p className="text-yellow-600 text-sm">Add an extra layer of security to your account</p>
                  </div>
                  <button className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors">
                    Enable 2FA
                  </button>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-black mb-3">Active Sessions</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-600 text-sm">Current session: This device</p>
              </div>
            </div>
          </div>
        );

      case 'usage':
        return (
          <div className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-black mb-2">Usage &amp; Limits</h3>
              <p className="text-gray-700 text-sm">
                Usage and limits are now shown in <span className="font-semibold">Billing &amp; Plans</span>.
              </p>
              <button
                onClick={() => setActiveTab('billing')}
                className="mt-3 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold"
              >
                Go to Billing &amp; Plans
              </button>
            </div>
          </div>
        );

      case 'notifications':
        // Only the real, persisted watchlist settings — the previous static
        // "Docket Processing Complete / Summary / Weekly Report" checkboxes
        // were uncontrolled defaultChecked inputs that saved nothing.
        return (
          <div className="space-y-6">
            <WatchlistNotificationSettings />
          </div>
        );

      case 'api-keys':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-black mb-3">API Access</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-blue-800">API Access</h4>
                    <p className="text-blue-600 text-sm">Access CommsGenie programmatically</p>
                  </div>
                  <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                    Generate API Key
                  </button>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-black mb-3">API Documentation</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-600 text-sm">API documentation will be available here.</p>
              </div>
            </div>
          </div>
        );

      case 'bookmarks':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-black mb-3">Saved Dockets</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-600 text-sm">Your bookmarked dockets will appear here.</p>
              </div>
            </div>
          </div>
        );

      case 'downloads':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-black mb-3">Download History</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-600 text-sm">Your download history will appear here.</p>
              </div>
            </div>
          </div>
        );

      case 'spam-filters':
        return (
          <div className="space-y-4">
            {userId ? (
              <SpamFilerSettings userId={userId} />
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-600 text-sm">Loading user information...</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8 text-center">
        <div className="mb-2">
          <h2 className="text-2xl font-bold text-black">
            Account Settings
          </h2>
        </div>
        <p className="text-gray-600 text-sm">
          Manage your account, security, and preferences
        </p>
      </div>

      {/* Tabbed Interface */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Sidebar - Tabs */}
        <div className="lg:w-72 flex-shrink-0">
          <div className="backdrop-blur-xl bg-white/90 border border-green-300/50 rounded-2xl p-3 shadow-lg">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-left transition-all duration-200 ${
                      isActive
                        ? 'bg-green-500 text-white shadow-sm'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{tab.label}</div>
                      <div className={`text-xs ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                        {tab.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Right Content Area */}
        <div className="flex-1">
          <div className="backdrop-blur-xl bg-white/90 border border-green-300/50 rounded-2xl p-6 shadow-lg">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};
